/**
 * PII deny-list — fields stripped before any event leaves the process for Sentry.
 *
 * system-decisions §6 mandates a three-tier PII classification. Sensitive PII
 * (Aadhaar, PAN, bank account, salary, employee bank/UPI) must never appear in
 * error reports — not in breadcrumbs, not in extras, not in stack-frame locals.
 *
 * The deny-list is matched case-insensitively against:
 *   - object keys at every depth
 *   - URL query string parameter names
 *   - HTTP header names
 *   - Sentry breadcrumb message fields named in the list
 *
 * Adding a new field here is a one-line, reviewer-friendly diff. Removing one
 * is a security-review-required change.
 */
export const PII_DENY_LIST: ReadonlyArray<string> = Object.freeze([
  // Sensitive PII — system-decisions §6, tier 1
  "pan",
  "pan_number",
  "aadhaar",
  "aadhaar_number",
  "bank_account",
  "bank_account_number",
  "bank_account_no",
  "account_number",
  "ifsc",
  "upi_id",
  "upi",
  "salary",
  "salary_amount",
  "gross_salary",
  "net_salary",
  "ctc",
  "employee_bank",

  // Authentication credentials — never in logs
  "password",
  "password_hash",
  "session_token",
  "cookie",
  "authorization",
  "api_key",
  "api_key_secret",
  "recovery_code",
  "otp",
  "otp_code",
  "pii_key",
  "webhook_secret",
  "signing_key",

  // OTP / step-up
  "mfa_code",
  "totp_seed",

  // Webhook signatures (vendor-specific)
  "x-razorpay-signature",
  "x-hub-signature-256",
  "x-msg91-signature",
]);

const denyMap = new Map<string, true>();
for (const k of PII_DENY_LIST) denyMap.set(k.toLowerCase(), true);

/** O(1) deny-list lookup. */
export function isDenied(fieldName: string): boolean {
  return denyMap.has(fieldName.toLowerCase());
}
